/**
 * **A low-sleep or low-energy task, read on the card** (attention-queue R22, R23, R27;
 * `docs/design/zadania-progi-2026-10-02/`, view 3): the client's recent readings as a plot — the
 * ones the verdict counts solid on a neutral ground, the earlier ones hollow beside them, their
 * average as one line against the trainer's dashed level — and on the desk two tiles naming the
 * counted readings and the newest of them.
 *
 * ── One chart family for sleep and energy ───────────────────────────────────────────────────
 * The plot is the Trendy tab's: the series in its own ink (sleep blue, energy ember), banded by
 * `wellbeingBanding` so a reading under the level is drawn in the danger ink there and here alike,
 * over KANON's wash. Every mark on it is the library's, through `ReadoutChart`: the level is a
 * `levels` line, the ground and the average a `span` — nothing is drawn by hand here.
 *
 * ── Nothing here judges ─────────────────────────────────────────────────────────────────────
 * Which readings count, and their average, are the server's (`model/vitalReadings`). The heading's
 * figure is the queue item's own; this block draws what stands behind it.
 *
 * ── One read, for a card the trainer can see ────────────────────────────────────────────────
 * `GET /vitals/customers/{id}/readings` is gated on the block being on screen, like every lazy read
 * on these cards. The legend is the item's own facts and stands from the first frame; the plot and
 * the tiles arrive together in a box that is already their height.
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_WELLBEING_LEVELS, wellbeingBanding, type WellbeingSignal } from '~/entities/client';
import { dateTimeFormat } from '~/shared/lib/intlCache';
import { useOnScreen } from '~/shared/lib/useOnScreen';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import {
  READOUT_STROKE_WIDTH,
  ReadoutChart,
  type ReadoutMargin,
  type ReadoutPlotSpec,
} from '~/shared/ui/chart/ReadoutChart';

import type { TaskDensity } from '../lib/taskWrites';
import { useVitalReadings } from '../lib/useVitalReadings';
import type { AttentionItem } from '../model/attentionItems';
import { vitalSpelling, weekdayDate, type VitalKind } from '../model/taskContent';
import type { VitalFacts } from '../model/triageShape';
import type { VitalReadings } from '../model/vitalReadings';
import { BlockRead } from './BlockRead';
import styles from './taskBlocks.module.css';
import own from './vitalBlock.module.css';

/** Blue is sleep, ember is energy — the two series' own inks, as every chart of them draws. */
const SERIES: Readonly<Record<VitalKind, string>> = {
  sleep: 'var(--chart-series-sleep)',
  energy: 'var(--chart-series-mood)',
};

/** Which wellbeing signal each kind is banded as — energy is the rating Trendy calls mood. */
const SIGNAL: Readonly<Record<VitalKind, WellbeingSignal>> = { sleep: 'sleep', energy: 'mood' };

/** A counted reading is a solid dot; an earlier one a small ring over the card's ground. */
const COUNTED_MARK = { radius: 4, filled: true } as const;
const CONTEXT_MARK = { radius: 3, filled: false } as const;

/**
 * The gutters: the right one holds the average's figure, which stands past the newest reading; the
 * left one the axis, whose ticks carry the unit — „6,0 h" for sleep, a bare rating for energy.
 * Numbers rather than tokens, because bklit computes the inner box from them (`ReadoutChart`).
 */
const MARGIN: Readonly<Record<VitalKind, ReadoutMargin>> = {
  sleep: { top: 8, right: 58, bottom: 20, left: 46 },
  energy: { top: 8, right: 58, bottom: 20, left: 34 },
};

/**
 * The x axis stands this share of the readings' span past each end — and never less than a day —
 * so the first and the newest reading are drawn whole and the ground behind the counted ones has
 * room on both sides, instead of being cut by the plot's edge.
 */
const SPAN_INSET = 0.06;
const MIN_INSET_MS = 86_400_000;

interface VitalBlockProps {
  readonly item: AttentionItem;
  readonly kind: VitalKind;
  readonly vital: VitalFacts;
  readonly density: TaskDensity;
  readonly reducedMotion: boolean;
}

export function VitalBlock({ item, kind, vital, density, reducedMotion }: VitalBlockProps) {
  const { t, i18n } = useTranslation();
  const boxRef = useRef<HTMLDivElement>(null);
  const { view, pending, failed } = useVitalReadings(item.subjectCustomerId, kind, useOnScreen(boxRef));
  const gate = useSkeletonGate(pending);
  const spell = vitalSpelling(kind, (key, values) => t(key, values), i18n.language);
  const desk = density === 'desk';

  const plot = view.readings.length === 0 ? null : (
    <VitalPlot
      view={view}
      kind={kind}
      level={vital.threshold}
      name={t(`web:tasks.blocks.vital.name.${kind}`)}
      // The desk's height is the block's own property: it steps down on a short window (`.desk`).
      height={desk ? 'var(--vital-plot-height)' : 'var(--h-vital-plot-quick)'}
      reducedMotion={reducedMotion}
    />
  );

  if (!desk) {
    return (
      <div className={own.readingQuick} ref={boxRef} aria-busy={gate.waiting}>
        <BlockRead gate={gate} reserve={own.reserveQuick} failed={failed}>
          {plot}
        </BlockRead>
      </div>
    );
  }

  const latest = view.latest;
  return (
    <div
      className={`${String(styles.stack)} ${String(styles.desk)} ${String(own.desk)}`}
      ref={boxRef}
      aria-busy={gate.waiting}
    >
      <div className={own.head}>
        <h4 className={styles.sectionTitle}>{t(`web:tasks.blocks.vital.title.${kind}`)}</h4>
        <p className={own.legend}>
          <span className={own.legendItem}>
            <span className={`${String(own.swatch)} ${String(own.swatchCounted)}`} aria-hidden="true" />
            {t('web:tasks.blocks.vital.legend.counted')}
          </span>
          <span className={own.legendItem}>
            <span className={own.swatch} aria-hidden="true" />
            {t('web:tasks.blocks.vital.legend.context')}
          </span>
          <span className={own.legendItem}>
            <span className={`${String(own.rule)} ${String(own.ruleMean)}`} aria-hidden="true" />
            {t('web:tasks.blocks.vital.legend.mean', { average: spell.withUnit(spell.average(vital.average)) })}
          </span>
          <span className={own.legendItem}>
            <span className={`${String(own.rule)} ${String(own.ruleLevel)}`} aria-hidden="true" />
            {t('web:tasks.content.vital.level', { level: spell.level(vital.threshold) })}
          </span>
        </p>
      </div>
      <div className={own.readingBox}>
        <BlockRead gate={gate} reserve={own.reserveDesk} failed={failed} className={own.reading}>
          {plot === null ? null : (
            <>
              {plot}
              <div className={`${String(styles.tiles)} ${String(own.tiles)}`}>
                <div className={styles.tile}>
                  <p className={styles.tileLabel}>{t('web:tasks.blocks.vital.tile.counted')}</p>
                  <p className={styles.tileValue}>
                    {spell.withUnit(view.counted.map((reading) => spell.reading(reading.value)).join(' · '))}
                  </p>
                </div>
                {latest === null ? null : (
                  <div className={styles.tile}>
                    <p className={styles.tileLabel}>{t('web:tasks.blocks.vital.tile.latest')}</p>
                    <p className={styles.tileValue}>
                      {[
                        kind === 'energy'
                          ? t('web:tasks.blocks.vital.outOfTen', { value: spell.reading(latest.value) })
                          : spell.withUnit(spell.reading(latest.value)),
                        weekdayDate(latest.localDay, i18n.language),
                      ].join(' · ')}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </BlockRead>
      </div>
    </div>
  );
}

interface VitalPlotProps {
  readonly view: VitalReadings;
  readonly kind: VitalKind;
  readonly level: number;
  readonly name: string;
  readonly height: string;
  readonly reducedMotion: boolean;
}

function VitalPlot({ view, kind, level, name, height, reducedMotion }: VitalPlotProps) {
  const { t, i18n } = useTranslation();
  const spell = vitalSpelling(kind, (key, values) => t(key, values), i18n.language);
  const colour = SERIES[kind];
  const counted = new Set(view.counted.map((reading) => reading.at.getTime()));
  const dayOf = new Map(view.readings.map((reading) => [reading.at.getTime(), reading.localDay]));
  /** The days that hold more than one reading: there the day alone does not say which one. */
  const sharedDays = new Set(
    view.readings
      .map((reading) => reading.localDay)
      .filter((day, index, days) => days.indexOf(day) !== index),
  );
  const countedNote = [t('web:tasks.blocks.vital.countedNote')];
  const first = view.counted[0];
  const oldest = view.readings[0]?.at.getTime() ?? 0;
  const newest = view.readings[view.readings.length - 1]?.at.getTime() ?? oldest;
  const inset = Math.max((newest - oldest) * SPAN_INSET, MIN_INSET_MS);

  const plot: ReadoutPlotSpec = {
    margin: MARGIN[kind],
    // Four rules, not Trendy's three: on the short plot a rating's whole steps then leave a single
    // label. The labels themselves are Trendy's small tertiary ones — one look for these charts.
    yTicks: 4,
    yTickStyle: 'unit',
    quietTicks: true,
    xTicks: 'span',
    area: true,
    strokeWidth: READOUT_STROKE_WIDTH,
    markAt: (at) => (counted.has(at.getTime()) ? COUNTED_MARK : CONTEXT_MARK),
    levels: [level],
    // The average is the server's (`model/vitalReadings`); the chart only draws it.
    ...(first === undefined || view.latest === null
      ? {}
      : {
          span: {
            from: first.at,
            to: view.latest.at,
            mean:
              view.average === null
                ? null
                : { value: view.average, label: spell.withUnit(spell.average(view.average)) },
          },
        }),
    // Which readings the average is taken over is told by ink on the plot; in the readout, to the
    // keyboard and in the table it is told in words.
    notesAt: (at) => (counted.has(at.getTime()) ? countedNote : []),
    // A reading is named by the CLIENT's day it was taken on (R10), not by the reader's reading of
    // its instant; an axis label between two readings has no such day and keeps the instant's. Two
    // readings of one day are told apart by their hour.
    xAt: (at) => {
      const day = dayOf.get(at.getTime());
      if (day === undefined) return dateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }).format(at);
      const named = weekdayDate(day, i18n.language);
      return sharedDays.has(day)
        ? `${named}, ${dateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }).format(at)}`
        : named;
    },
  };

  return (
    <ReadoutChart
      points={view.readings.map((reading) => ({ x: reading.at, y: reading.value }))}
      colour={colour}
      // The task's own level is the low cut, so the plot and the heading's verdict agree.
      banding={wellbeingBanding(colour, SIGNAL[kind], { ...DEFAULT_WELLBEING_LEVELS, [SIGNAL[kind]]: level })}
      height={height}
      name={name}
      unit={kind === 'sleep' ? t('web:tasks.content.vital.hour') : undefined}
      whole={kind === 'energy'}
      zeroBaseline={false}
      xDomain={[new Date(oldest - inset), new Date(newest + inset)]}
      // The level and the average are lines on this plot, so the axis has to enclose both.
      extraYValues={view.average === null ? [level] : [level, view.average]}
      plot={plot}
      reducedMotion={reducedMotion}
    />
  );
}
